import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";

export default function Doctors() {
  const [searchParams] = useSearchParams();
  const [doctors, setDoctors] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [specialty, setSpecialty] = useState(searchParams.get("specialty") || "All");

  useEffect(() => {
    Promise.all([api.get("/doctors"), api.get("/specialties")]).then(([d, s]) => {
      setDoctors(d.data); setSpecialties(s.data);
    });
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return doctors.filter((d) => {
      const ok = specialty === "All" || d.specialty === specialty;
      const m = !q || d.name.toLowerCase().includes(q) || d.specialty.toLowerCase().includes(q);
      return ok && m;
    });
  }, [doctors, query, specialty]);

  return (
    <div data-testid="doctors-page">
      <section className="hero-bg py-12">
        <div className="container-x">
          <span className="font-cursive text-2xl text-brand-purple">Our Doctors</span>
          <h1 className="text-4xl sm:text-5xl font-bold">Find the right specialist</h1>
          <p className="text-slate-600 mt-3 max-w-2xl">Filter by specialty or search by name — every doctor at One Hospitals is board-certified and committed to evidence-based care.</p>

          <div className="mt-6 flex flex-col md:flex-row gap-3 max-w-3xl">
            <div className="flex-1 flex items-center bg-white rounded-full border border-slate-200 p-1 shadow-sm">
              <Search className="h-5 w-5 text-slate-400 ml-3" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search doctor or specialty…"
                className="!border-0 !bg-transparent !shadow-none !ring-0 flex-1 !py-2.5" data-testid="doctors-search-input" />
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2" data-testid="specialty-filters">
            {["All", ...specialties.map((s) => s.name)].map((n) => (
              <button key={n} onClick={() => setSpecialty(n)}
                className={`px-4 py-1.5 text-sm rounded-full border transition ${specialty === n ? "bg-brand-purple text-white border-brand-purple" : "bg-white border-slate-200 hover:border-brand-purple text-slate-700"}`}
                data-testid={`spec-filter-${n.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}>
                {n}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x py-10">
        <div className="text-sm text-slate-500 mb-4">{filtered.length} doctors found</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((d) => (
            <div key={d.id} className="card-clean overflow-hidden" data-testid={`doctor-card-${d.id}`}>
              <img src={d.photo} alt={d.name} className="w-full h-56 object-cover" />
              <div className="p-5">
                <div className="font-semibold text-lg">{d.name}</div>
                <div className="text-sm text-brand-purple font-medium">{d.specialty}</div>
                <div className="text-xs text-slate-500 mt-1">{d.qualification}</div>
                <div className="text-xs text-slate-500 mt-2 leading-relaxed">
                  <div>{d.experience_years}+ years • {d.timings}</div>
                  <div>Languages: {d.languages.join(", ")}</div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Link to={`/book-appointment?doctor=${d.id}`} className="btn-primary !py-2 !px-3 text-xs flex-1 justify-center" data-testid={`book-${d.id}`}>Book</Link>
                  <Link to={`/second-opinion?doctor=${d.id}`} className="btn-secondary !py-2 !px-3 text-xs flex-1 justify-center" data-testid={`opinion-${d.id}`}>2nd Opinion</Link>
                  <Link to={`/doctors/${d.id}`} className="btn-outline !py-2 !px-3 text-xs justify-center" data-testid={`profile-${d.id}`}>Profile</Link>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="col-span-full text-center py-12 text-slate-500">No doctors match your filters.</div>}
        </div>
      </section>
    </div>
  );
}
